"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChangeHistoryTimeline = ChangeHistoryTimeline;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("@iconify/react");
var date_fns_1 = require("date-fns");
var react_2 = require("react");
function formatIso(iso) {
    if (!iso)
        return "";
    try {
        return (0, date_fns_1.format)(new Date(iso), "dd MMM yyyy");
    }
    catch (_a) {
        return iso;
    }
}
function formatRangeLabel(col) {
    var fromLabel = col.fromIso ? formatIso(col.fromIso) : "—";
    var toLabel = col.toIso ? formatIso(col.toIso) : "TD";
    return "".concat(fromLabel, " \u2013 ").concat(toLabel);
}
function formatCapturedAt(event) {
    if (!event)
        return "—";
    try {
        return (0, date_fns_1.format)(new Date(event.createdAt), "dd MMM yyyy, HH:mm");
    }
    catch (_a) {
        return event.createdAt;
    }
}
function performerName(event) {
    var _a;
    if (!event)
        return "—";
    var u = event.performedByUser;
    if (u)
        return (_a = ("".concat(u.firstName, " ").concat(u.lastName).trim() || u.name)) !== null && _a !== void 0 ? _a : u.email;
    return event.performedBy;
}
function formatValue(v) {
    if (v === null || v === undefined || v === "")
        return "—";
    if (typeof v === "string")
        return v;
    if (typeof v === "number" || typeof v === "boolean")
        return String(v);
    try {
        var json = JSON.stringify(v, function (k, val) { return (k === "_id" ? undefined : val); });
        return json.length > 90 ? "".concat(json.slice(0, 90), "\u2026") : json;
    }
    catch (_a) {
        return String(v);
    }
}
function buildColumns(events) {
    var _a;
    // Only Updates appear in business-user history per the ticket. Sort
    // chronologically by effective date (fall back to createdAt when missing).
    var updates = events.filter(function (e) { return e.action === "update"; });
    if (updates.length === 0)
        return [];
    var sorted = __spreadArray([], updates, true).sort(function (a, b) {
        var ad = new Date(a.effectiveDate || a.createdAt).getTime();
        var bd = new Date(b.effectiveDate || b.createdAt).getTime();
        return ad - bd;
    });
    var columns = [];
    // Genesis column — state before the first update. Use the earliest event's
    // `before` snapshot when available.
    var first = sorted[0];
    if (first.before) {
        columns.push({
            fromIso: null,
            toIso: first.effectiveDate,
            event: null,
            values: first.before,
            changedFields: new Set(),
        });
    }
    for (var i = 0; i < sorted.length; i++) {
        var ev = sorted[i];
        var next = sorted[i + 1];
        columns.push({
            fromIso: ev.effectiveDate,
            toIso: next ? next.effectiveDate : null,
            event: ev,
            values: (_a = ev.after) !== null && _a !== void 0 ? _a : {},
            changedFields: new Set(ev.changes.map(function (c) { return c.field; })),
        });
    }
    return columns;
}
function ChangeHistoryTimeline(props) {
    var events = props.events, fields = props.fields;
    var columns = (0, react_2.useMemo)(function () { return buildColumns(events); }, [events]);
    // Group fields by their `group` to render section headers.
    var grouped = (0, react_2.useMemo)(function () {
        var _a;
        var out = new Map();
        for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
            var f = fields_1[_i];
            var k = (_a = f.group) !== null && _a !== void 0 ? _a : "Other";
            if (!out.has(k))
                out.set(k, []);
            out.get(k).push(f);
        }
        return Array.from(out.entries());
    }, [fields]);
    if (columns.length === 0) {
        return ((0, jsx_runtime_1.jsxs)("div", { className: "flex flex-col items-center gap-2 py-10 text-gray-400", children: [(0, jsx_runtime_1.jsx)(react_1.Icon, { icon: "solar:history-linear", className: "text-3xl" }), (0, jsx_runtime_1.jsx)("span", { className: "text-sm", children: "No updates recorded yet \u2014 the timeline appears once an Update has been saved." })] }));
    }
    var totalCols = columns.length + 1; // +1 for the field-label column
    return ((0, jsx_runtime_1.jsx)("div", { className: "overflow-x-auto rounded-lg border border-gray-200", children: (0, jsx_runtime_1.jsxs)("table", { className: "min-w-full text-left text-xs", children: [(0, jsx_runtime_1.jsx)("thead", { className: "bg-gray-50", children: (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("th", { className: "sticky left-0 z-10 min-w-[220px] bg-gray-50 px-3 py-2 font-semibold text-gray-700", children: "Change Application Dates" }), columns.map(function (col, i) { return ((0, jsx_runtime_1.jsx)("th", { className: "min-w-[200px] border-l border-gray-200 px-3 py-2 font-mono font-semibold text-gray-700", children: formatRangeLabel(col) }, "hdr-".concat(i))); })] }) }), (0, jsx_runtime_1.jsxs)("tbody", { children: [(0, jsx_runtime_1.jsxs)("tr", { className: "bg-white", children: [(0, jsx_runtime_1.jsx)("td", { className: "sticky left-0 z-10 bg-white px-3 py-2 italic text-gray-500", children: "Change Capturing Date" }), columns.map(function (col, i) { return ((0, jsx_runtime_1.jsx)("td", { className: "border-l border-gray-100 px-3 py-2 text-gray-600", children: formatCapturedAt(col.event) }, "cap-".concat(i))); })] }), (0, jsx_runtime_1.jsxs)("tr", { className: "bg-white", children: [(0, jsx_runtime_1.jsx)("td", { className: "sticky left-0 z-10 bg-white px-3 py-2 italic text-gray-500", children: "Change Capturing User" }), columns.map(function (col, i) { return ((0, jsx_runtime_1.jsx)("td", { className: "border-l border-gray-100 px-3 py-2 text-gray-600", children: performerName(col.event) }, "usr-".concat(i))); })] }), (0, jsx_runtime_1.jsxs)("tr", { className: "bg-white", children: [(0, jsx_runtime_1.jsx)("td", { className: "sticky left-0 z-10 bg-white px-3 py-2 italic text-gray-500", children: "Reason / note" }), columns.map(function (col, i) {
                                    var _a, _b;
                                    return ((0, jsx_runtime_1.jsx)("td", { className: "border-l border-gray-100 px-3 py-2 text-gray-600", children: (_b = (_a = col.event) === null || _a === void 0 ? void 0 : _a.reason) !== null && _b !== void 0 ? _b : "—" }, "reason-".concat(i)));
                                })] }), grouped.map(function (_a) {
                            var groupName = _a[0], groupFields = _a[1];
                            return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("tr", { className: "bg-gray-100", children: (0, jsx_runtime_1.jsx)("td", { colSpan: totalCols, className: "sticky left-0 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700", children: groupName }) }, "group-".concat(groupName)), groupFields.map(function (f) { return ((0, jsx_runtime_1.jsxs)("tr", { className: "bg-white hover:bg-gray-50", children: [(0, jsx_runtime_1.jsx)("td", { className: "sticky left-0 z-10 bg-white px-3 py-2 font-medium text-gray-700", children: f.label }), columns.map(function (col, i) {
                                                var changed = col.changedFields.has(f.key);
                                                return ((0, jsx_runtime_1.jsx)("td", { className: [
                                                        "border-l border-gray-100 px-3 py-2 align-top break-words",
                                                        changed ? "bg-primary-50 font-semibold text-primary-700" : "text-gray-700",
                                                    ].join(" "), title: changed ? "Changed in this version" : undefined, children: formatValue(col.values[f.key]) }, "cell-".concat(f.key, "-").concat(i)));
                                            })] }, "field-".concat(f.key))); })] }));
                        })] })] }) }));
}
